import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { type ReactElement, type Ref, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { NUMERIC_FONT } from '../../../app/theme';
import { Amount } from '../../../components/Amount';
import { StatusChip } from '../../../components/StatusChip';

import type { AccountSummaryView } from './trading-account-view';

/** Props of {@link AccountFound}. */
export interface AccountFoundProps {
  readonly summary: AccountSummaryView;
  /**
   * The card heading: "Account found. Is it yours?" or "Your trading account is connected."; `null`
   * for an unreachable account, whose card only repeats what is known (R16).
   */
  readonly title: string | null;
  /** Receives focus when the result arrives (R31). */
  readonly headingRef?: Ref<HTMLHeadingElement> | undefined;
}

// Focus can be moved to the heading when a result arrives (R31) without it joining the tab order.
const FOCUS_ONLY = -1;

/**
 * The account as the server found it (specs/S6-trading-account/spec.md R6, R7, R18, R30):
 * `MT5 · ••••4567 · Broker-Live`, the holder, the currency and the balance in the account currency,
 * with a "Demo account" chip when it is one. Only the last 4 digits are ever shown (I9, Q81).
 */
export function AccountFound({ summary, title, headingRef }: AccountFoundProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <Paper
      component="section"
      aria-labelledby={title === null ? undefined : headingId}
      variant="outlined"
      sx={{ p: 1.75, borderRadius: 3.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}
    >
      {title === null ? null : (
        <Typography
          id={headingId}
          component="h2"
          ref={headingRef}
          tabIndex={FOCUS_ONLY}
          sx={{ color: 'success.main', fontWeight: 600, fontSize: 15 }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: NUMERIC_FONT, fontWeight: 600 }}>
          {t('onboarding.tradingAccount.found.summary', {
            platform: summary.platform,
            last4: summary.last4,
            server: summary.server,
          })}
        </Typography>
        {summary.isDemo ? (
          <StatusChip tone="info">{t('onboarding.tradingAccount.found.demo')}</StatusChip>
        ) : null}
      </Box>
      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 1,
          m: 0,
          fontSize: 13,
        }}
      >
        <Box>
          <Box component="dt" sx={{ color: 'text.secondary' }}>
            {t('onboarding.tradingAccount.found.holder')}
          </Box>
          <Box component="dd" sx={{ m: 0, fontWeight: 600 }}>
            {summary.holderName}
          </Box>
        </Box>
        <Box>
          <Box component="dt" sx={{ color: 'text.secondary' }}>
            {t('onboarding.tradingAccount.found.currency')}
          </Box>
          <Box component="dd" sx={{ m: 0, fontWeight: 600 }}>
            {summary.currency}
          </Box>
        </Box>
        <Box>
          <Box component="dt" sx={{ color: 'text.secondary' }}>
            {t('onboarding.tradingAccount.found.balance')}
          </Box>
          <Box component="dd" sx={{ m: 0, fontWeight: 600 }}>
            {summary.balance === null ? (
              t('onboarding.tradingAccount.found.balanceUnavailable')
            ) : (
              <Amount
                amountMinor={summary.balance.amountMinor}
                currency={summary.balance.currency}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
